import { useMethodGrouping } from 'hooks/useMethodGrouping';
import { useMethodGroupingRoute } from 'hooks/useMethodGroupingRoute';
import type { FC } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { UIPaymentGroups, UIPayoutGroups } from 'store/formatters';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { PayGroupCategories, PayoutGroupCategories } from 'store/models';
import { autoSelectTriggeredSelector } from 'store/slices/autoFlow';
import {
  depositMethodsSelector,
  paygroupCategoriesSelector,
  setCurrentMethod as setDepositCurrentMethod,
  shouldSkipInitFeatureSelector as depositSkipSelector,
} from 'store/slices/deposit';
import { paymentFlowSelector } from 'store/slices/global';
import {
  payoutGroupCategoriesSelector,
  setCurrentMethod as setWithdrawalCurrentMethod,
  shouldSkipInitFeatureSelector as withdrawalSkipSelector,
  withdrawalMethodsSelector,
} from 'store/slices/withdrawal';
import type { CategoryConfig, GroupedMethodsByCategory } from 'utils';
import { isDepositFlowType } from 'utils';

import type { TabsData } from '../Modulor/MethodGrouping/CategoryTabs';
import { useAppCore } from './AppCoreProvider/AppCoreContext';

interface MethodGroupingContextValue {
  categoryConfig: CategoryConfig;
  groupedMethodsByCategory: GroupedMethodsByCategory[];
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  carouselTabs: TabsData[];
  handleCategorySelect: (categoryName: string) => void;
  visibleCategories: GroupedMethodsByCategory[];
  disabledCategory: GroupedMethodsByCategory | null;
  saveUserSelection: (categoryName: string, payGroupId: number) => void;
  getUserSelectedGroup: (categoryName: string) => number | null;
}

const MethodGroupingContext = createContext<MethodGroupingContextValue | null>(null);

/**
 * Provides method grouping data to all children.
 * Calls useMethodGrouping once with carousel config — all consumers read from context.
 * Handles both deposit and withdrawal flows.
 */
export const MethodGroupingProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const flowType = useAppSelector(paymentFlowSelector);
  const isDeposit = isDepositFlowType(flowType);
  const autoSelectTriggered = useAppSelector(autoSelectTriggeredSelector);
  const paygroupCategories = useAppSelector(isDeposit ? paygroupCategoriesSelector : payoutGroupCategoriesSelector);
  const methods = useAppSelector(isDeposit ? depositMethodsSelector : withdrawalMethodsSelector);
  const shouldSkipFeature = useAppSelector(isDeposit ? depositSkipSelector : withdrawalSkipSelector);
  // Get carouselMethodList from AppCore init data
  const { widgetData } = useAppCore();
  const carouselMethodList = widgetData?.data?.carouselMethodList || [];
  const methodToRedirect = widgetData?.data?.methodToRedirect;
  // Track route for visibility flags
  useMethodGroupingRoute();

  const result = useMethodGrouping({
    methods,
    paygroupCategories: paygroupCategories as PayGroupCategories | PayoutGroupCategories,
    shouldSkipFeature,
    carousel: {
      carouselMethodList,
      onMethodPreselect: (method) => {
        if (isDeposit) {
          dispatch(setDepositCurrentMethod(method as UIPaymentGroups));
        } else {
          dispatch(setWithdrawalCurrentMethod(method as UIPayoutGroups));
        }
      },
      isAutoSelectBlocked: autoSelectTriggered,
      methodToRedirect,
    },
  });

  const value = useMemo(
    () => result,
    [
      result.categoryConfig,
      result.groupedMethodsByCategory,
      result.selectedCategory,
      result.carouselTabs,
      result.visibleCategories,
      result.disabledCategory,
    ],
  );

  return <MethodGroupingContext.Provider value={value}>{children}</MethodGroupingContext.Provider>;
};

/**
 * Hook to consume method grouping context.
 * Must be used inside MethodGroupingProvider.
 */
export const useMethodGroupingContext = (): MethodGroupingContextValue => {
  const context = useContext(MethodGroupingContext);
  if (!context) {
    throw new Error('useMethodGroupingContext must be used within MethodGroupingProvider');
  }
  return context;
};
